import type { ExpiringItemView, RecipeIngredient, RecipeSuggestion } from "../types/dashboard";
import {
    formatIngredientExpiryLabel,
    getIngredientExpiryStatus,
    type IngredientExpiryStatus,
} from "./recipeIngredients";

/** Portrait mobile card — width stays narrow so saved images read well on phones. */
const IMAGE_WIDTH = 480;
const MARGIN = 24;
const CARD_PAD = 32;
const CONTENT_WIDTH = IMAGE_WIDTH - MARGIN * 2 - CARD_PAD * 2;
/** ~50–55 characters at 15px — standard comfortable reading measure. */
const TEXT_MAX_WIDTH = 320;
const STEP_INDENT = 28;
const SCALE = 2;

function proseOffset(): number {
    return Math.max(0, (CONTENT_WIDTH - TEXT_MAX_WIDTH) / 2);
}

const COLORS = {
    bg: "#f6f4f0",
    surface: "#ffffff",
    text: "#2c2a29",
    muted: "rgba(44, 42, 41, 0.55)",
    primary: "#7a8b76",
    clay: "#b85c38",
    chipBg: "#f0ede8",
    chipBorder: "rgba(44, 42, 41, 0.12)",
    expiringBg: "rgba(184, 92, 56, 0.12)",
    expiringBorder: "rgba(184, 92, 56, 0.35)",
    divider: "rgba(44, 42, 41, 0.1)",
};

interface ChipLayout {
    text: string;
    status: IngredientExpiryStatus | null;
    width: number;
}

interface ChipRow {
    chips: ChipLayout[];
}

function slugifyDish(dish: string): string {
    return (
        dish
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 60) || "recipe"
    );
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let line = "";

    for (const word of words) {
        const next = line ? `${line} ${word}` : word;
        if (ctx.measureText(next).width > maxWidth && line) {
            lines.push(line);
            line = word;
        } else {
            line = next;
        }
    }

    if (line) lines.push(line);
    return lines.length ? lines : [""];
}

function layoutChips(
    ctx: CanvasRenderingContext2D,
    ingredients: RecipeIngredient[],
    expiringItems: ExpiringItemView[],
): ChipRow[] {
    const chipPadX = 10;
    const gap = 4;
    const rows: ChipRow[] = [];
    let current: ChipLayout[] = [];
    let rowWidth = 0;

    ctx.font = '600 12px "Inter", system-ui, sans-serif';

    for (const ing of ingredients) {
        const status = getIngredientExpiryStatus(ing, expiringItems);
        const text = formatIngredientExpiryLabel(ing, expiringItems);
        const width = ctx.measureText(text).width + chipPadX * 2;
        const addedWidth = current.length ? gap + width : width;

        if (current.length && rowWidth + addedWidth > CONTENT_WIDTH) {
            rows.push({ chips: current });
            current = [{ text, status, width }];
            rowWidth = width;
        } else {
            rowWidth += addedWidth;
            current.push({ text, status, width });
        }
    }

    if (current.length) rows.push({ chips: current });
    return rows;
}

function measureLayout(
    ctx: CanvasRenderingContext2D,
    recipe: RecipeSuggestion,
    expiringItems: ExpiringItemView[],
): number {
    let y = MARGIN + CARD_PAD;

    ctx.font = '700 26px "Playfair Display", Georgia, serif';
    const titleLines = wrapText(ctx, recipe.dish, CONTENT_WIDTH);
    y += titleLines.length * 30 + 16;

    y += 1 + 16;

    ctx.font = 'italic 400 15px "Inter", system-ui, sans-serif';
    const summaryLines = wrapText(ctx, recipe.summary, TEXT_MAX_WIDTH);
    y += summaryLines.length * 22 + 20;

    y += 11 + 8;

    const chipRows = layoutChips(ctx, recipe.ingredients, expiringItems);
    y += chipRows.length * 28 + (chipRows.length ? -4 : 0) + 20;

    ctx.font = '700 15px "Playfair Display", Georgia, serif';
    y += 18 + 8;

    ctx.font = '400 15px "Inter", system-ui, sans-serif';
    for (const step of recipe.steps) {
        const lines = wrapText(ctx, step, TEXT_MAX_WIDTH - STEP_INDENT);
        y += lines.length * 22 + 8;
    }

    y += 12 + 1 + 16;
    ctx.font = '700 11px "Inter", system-ui, sans-serif';
    y += 14;

    return y + CARD_PAD + MARGIN;
}

async function ensureFonts(): Promise<void> {
    await Promise.all([
        document.fonts.load('700 26px "Playfair Display"'),
        document.fonts.load('700 15px "Playfair Display"'),
        document.fonts.load('400 15px "Inter"'),
        document.fonts.load('italic 400 15px "Inter"'),
        document.fonts.load('600 12px "Inter"'),
        document.fonts.load('600 11px "Inter"'),
        document.fonts.load('700 11px "Inter"'),
    ]);
    await document.fonts.ready;
}

function drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
): void {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
}

function drawRecipeImage(
    ctx: CanvasRenderingContext2D,
    recipe: RecipeSuggestion,
    expiringItems: ExpiringItemView[],
    height: number,
): void {
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, IMAGE_WIDTH, height);

    const cardX = MARGIN;
    const cardY = MARGIN;
    const cardW = IMAGE_WIDTH - MARGIN * 2;
    const cardH = height - MARGIN * 2;

    ctx.save();
    ctx.shadowColor = "rgba(44, 42, 41, 0.08)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = COLORS.surface;
    drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 12);
    ctx.fill();
    ctx.restore();

    let y = cardY + CARD_PAD;
    const x = cardX + CARD_PAD;
    const proseX = x + proseOffset();

    ctx.fillStyle = COLORS.text;
    ctx.font = '700 26px "Playfair Display", Georgia, serif';
    for (const line of wrapText(ctx, recipe.dish, CONTENT_WIDTH)) {
        ctx.fillText(line, x, y + 22);
        y += 30;
    }
    y += 16;

    ctx.strokeStyle = COLORS.divider;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + CONTENT_WIDTH, y);
    ctx.stroke();
    y += 16;

    ctx.fillStyle = COLORS.muted;
    ctx.font = 'italic 400 15px "Inter", system-ui, sans-serif';
    for (const line of wrapText(ctx, recipe.summary, TEXT_MAX_WIDTH)) {
        ctx.fillText(line, proseX, y + 15);
        y += 22;
    }
    y += 20;

    ctx.fillStyle = COLORS.muted;
    ctx.font = '600 11px "Inter", system-ui, sans-serif';
    ctx.fillText("INGREDIENTS", x, y);
    y += 19;

    const chipRows = layoutChips(ctx, recipe.ingredients, expiringItems);
    const chipGap = 4;
    const chipH = 24;

    for (const row of chipRows) {
        let chipX = x;
        for (const chip of row.chips) {
            ctx.font = '600 12px "Inter", system-ui, sans-serif';
            if (chip.status === "expired") {
                ctx.fillStyle = "rgba(184, 92, 56, 0.2)";
                drawRoundedRect(ctx, chipX, y, chip.width, chipH, 999);
                ctx.fill();
                ctx.strokeStyle = "rgba(184, 92, 56, 0.5)";
            } else if (chip.status === "expiring") {
                ctx.fillStyle = COLORS.expiringBg;
                drawRoundedRect(ctx, chipX, y, chip.width, chipH, 999);
                ctx.fill();
                ctx.strokeStyle = COLORS.expiringBorder;
            } else {
                ctx.fillStyle = COLORS.chipBg;
                drawRoundedRect(ctx, chipX, y, chip.width, chipH, 999);
                ctx.fill();
                ctx.strokeStyle = COLORS.chipBorder;
            }
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.fillStyle = chip.status ? COLORS.clay : COLORS.text;
            ctx.fillText(chip.text, chipX + 10, y + 16);
            chipX += chip.width + chipGap;
        }
        y += chipH + chipGap;
    }
    y += 16;

    ctx.fillStyle = COLORS.muted;
    ctx.font = '700 15px "Playfair Display", Georgia, serif';
    ctx.fillText("Steps", x, y);
    y += 22;

    ctx.fillStyle = COLORS.text;
    ctx.font = '400 15px "Inter", system-ui, sans-serif';
    for (let i = 0; i < recipe.steps.length; i++) {
        const stepLines = wrapText(ctx, recipe.steps[i], TEXT_MAX_WIDTH - STEP_INDENT);
        ctx.font = '700 15px "Inter", system-ui, sans-serif';
        ctx.fillText(String(i + 1), proseX, y + 15);
        ctx.font = '400 15px "Inter", system-ui, sans-serif';
        for (const line of stepLines) {
            ctx.fillText(line, proseX + STEP_INDENT, y + 15);
            y += 22;
        }
        y += 8;
    }

    y += 4;
    ctx.strokeStyle = COLORS.divider;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + CONTENT_WIDTH, y);
    ctx.stroke();
    y += 16;

    ctx.fillStyle = COLORS.primary;
    ctx.font = '700 11px "Inter", system-ui, sans-serif';
    ctx.fillText(
        "PANTRY PAL",
        x + CONTENT_WIDTH / 2 - ctx.measureText("PANTRY PAL").width / 2,
        y + 10,
    );
}

export async function downloadRecipe(
    recipe: RecipeSuggestion,
    expiringItems: ExpiringItemView[],
): Promise<void> {
    await ensureFonts();

    const measureCanvas = document.createElement("canvas");
    const measureCtx = measureCanvas.getContext("2d")!;
    const height = measureLayout(measureCtx, recipe, expiringItems);

    const canvas = document.createElement("canvas");
    canvas.width = IMAGE_WIDTH * SCALE;
    canvas.height = height * SCALE;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(SCALE, SCALE);

    drawRecipeImage(ctx, recipe, expiringItems, height);

    const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, "image/png");
    });
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slugifyDish(recipe.dish)}.png`;
    link.click();
    URL.revokeObjectURL(url);
}
